import React, { useState, useEffect } from 'react'
import ReactSVG from 'react-svg'
import { propType } from 'graphql-anywhere'
import classNames from 'classnames'
import PropTypes from 'prop-types'
import ModalContainer from '../../components/modal-container'
import CONTENT_ITEM_FRAGMENT from '../../../graphql/fragments/content-item.gql'
import styles from './player-banner-rate-modal.css'
import {
  useUserRatingQuery,
  useUserRatingMutation
} from './user-rating-query-mutation'
import closeIcon from '../../../images/close-icon-no-color.svg'
import {
  UserRatingScale,
  getRatingValueToUpdate,
  shouldDisplayPopover
} from './shared'
import RateOption from './rate-option'

// contentItem is series or movie
const PlayerBannerRateModal = React.memo(
  ({ contentItem, dismissRateModal, setIsBannerRateModalShowing }) => {
    if (!contentItem) return null

    const [visible, setVisible] = useState(true)
    const [userRating, setRating] = useState(null)
    const [isPopoverOpen, setPopoverOpen] = useState(false)
    const [popeverText, setPopeverText] = useState('')

    const headerCopy = `How would you rate ${contentItem.title}?`
    const subHeaderCopy = `Let us know for better recommendations`

    // let other player components know the rate modal is showing or not during mount/unmount
    useEffect(() => {
      setIsBannerRateModalShowing(true)
      return () => {
        setIsBannerRateModalShowing(false)
      }
    }, [])

    useUserRatingQuery({
      contentItemId: contentItem.id,
      setRating
    })

    const { userRatingMutation } = useUserRatingMutation({ setRating })

    /**
     * When `RateOption` clicked, highlight the rate option, display the popover
     * @param {'NOT_MY_THING' | 'SOLID_WATCH' | 'TOP_PICK'} ratingValueClicked - ref to {@link UserRatingScale}
     */
    const onRateOptionClicked = ratingValueClicked => {
      const ratingValueToUpdate = getRatingValueToUpdate(
        userRating,
        ratingValueClicked
      )
      userRatingMutation({
        variables: {
          input: {
            contentItemId: contentItem.id,
            userRating: ratingValueToUpdate
          }
        }
      })
      const { shouldDipslayPopover, popoverCopy } = shouldDisplayPopover(
        ratingValueToUpdate
      )
      if (shouldDipslayPopover) {
        setPopoverOpen(true)
        setPopeverText(popoverCopy)
      } else {
        setPopoverOpen(false)
      }
    }

    const dismissModal = () => {
      setVisible(false)
      dismissRateModal()
    }

    return (
      <>
        {visible && (
          <ModalContainer
            noBackgroundColor
            onRequestClose={dismissModal}
          >
            <div className={styles.playerRateModal}>
              <div className={styles.header}>{headerCopy}</div>
              <div className={styles.subHeader}>{subHeaderCopy}</div>
              <div className={styles.rateOptionsRow}>
                <RateOption
                  defaultScale={UserRatingScale.NOT_MY_THING}
                  userRating={userRating}
                  onRateClick={onRateOptionClicked}
                  inPlayer
                  inPlayerRateModal
                />
                <RateOption
                  defaultScale={UserRatingScale.SOLID_WATCH}
                  userRating={userRating}
                  onRateClick={onRateOptionClicked}
                  inPlayer
                  inPlayerRateModal
                />
                <RateOption
                  defaultScale={UserRatingScale.TOP_PICK}
                  userRating={userRating}
                  onRateClick={onRateOptionClicked}
                  inPlayer
                  inPlayerRateModal
                />
              </div>
              <div
                className={styles.popover}
                style={{ visibility: isPopoverOpen ? 'visible' : 'hidden' }}
              >
                {popeverText}
                <div className={styles.popoverArrow} />
              </div>
              <div className={styles.closeIconWrapper} onClick={dismissModal}>
                <ReactSVG
                  src={closeIcon}
                  beforeInjection={svg => {
                    svg.setAttribute('class', classNames(styles.closeIcon))
                  }}
                />
              </div>
            </div>
          </ModalContainer>
        )}
      </>
    )
  }
)

PlayerBannerRateModal.propTypes = {
  contentItem: propType(CONTENT_ITEM_FRAGMENT).isRequired,
  dismissRateModal: PropTypes.func.isRequired,
  setIsBannerRateModalShowing: PropTypes.func.isRequired
}

export default PlayerBannerRateModal
